import { useState } from 'react';
import { SelectedFileCallback } from './BizMediabankContext';
import MediabankDialogSelect from './BizMediabankDialogSelect';
import BizMediabankDialogUpload from './BizMediabankDialogUpload';
import './biz-mediabank.scss';
import { MediabankFileType, MediabankSettings, MediabankUploadLocation, OrientationType } from '../../Types/mediabank';
import BizUnsplash from './BizUnsplash';
import { getPortalSetting } from '../../Services/portal';
import { MediaContentCategory } from '../../Types/mediaCatalog';
import { useTranslation } from 'react-i18next';

export type MediabankDialogMode = 'SELECT' | 'UPLOAD' | 'UNSPLASH';

type MediabankDialogProps = {
	onClose: SelectedFileCallback;
	fileTypes: MediabankFileType[];
	uploadLocation: MediabankUploadLocation;
	settings?: MediabankSettings;
	legacy?: boolean;
	mode?: MediabankDialogMode;
	orientation?: OrientationType;
	initialCategory?: MediaContentCategory;
};

const MediabankDialog = ({ onClose, fileTypes, uploadLocation, settings, legacy, mode, orientation, initialCategory }: MediabankDialogProps) => {
	const { t } = useTranslation();
	const [expandedPanel, setExpandedPanel] = useState<MediabankDialogMode | null>(mode ?? 'SELECT');
	const togglePanel = (panel: MediabankDialogMode) => {
		setExpandedPanel((current) => (current === panel ? null : panel));
	};

	return (
		<div className='bizmediabank-react-dialog-wrapper admin-primary'>
			<div className='bizmediabank-react-dialog'>
				{(mode === undefined || mode === 'UPLOAD') && (
					<div className={`bizmediabank-panel ${mode === 'UPLOAD' ? 'mode-upload' : ''}`}>
						<button
							type='button'
							className='panel-toggle'
							aria-expanded={expandedPanel === 'UPLOAD'}
							onClick={() => togglePanel('UPLOAD')}
						>
							<span>{t('MEDIABANK.UPLOAD_FILE')}</span>
							<i
								aria-hidden='true'
								className={expandedPanel === 'UPLOAD' ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'}
							/>
						</button>
						{expandedPanel === 'UPLOAD' && (
							<BizMediabankDialogUpload
								onClose={onClose}
								fileTypes={fileTypes}
								uploadLocation={uploadLocation}
								settings={settings}
								legacy={legacy}
							/>
						)}
					</div>
				)}
				{(mode === undefined || mode === 'SELECT') && (
					<div className={`bizmediabank-panel margin-top ${mode === 'SELECT' ? 'mode-select' : ''}`}>
						<button
							type='button'
							className='panel-toggle'
							aria-expanded={expandedPanel === 'SELECT'}
							onClick={() => togglePanel('SELECT')}
						>
							<span>{t('MEDIABANK.SELECT_EXISTING_FILE')}</span>
							<i
								aria-hidden='true'
								className={expandedPanel === 'SELECT' ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'}
							/>
						</button>
						{expandedPanel === 'SELECT' && (
							<MediabankDialogSelect
								onClose={onClose}
								initialType={fileTypes.length === 1 && fileTypes[0].id > 0 ? fileTypes[0].id : undefined}
								initialCategory={initialCategory}
								legacy={legacy}
							/>
						)}
					</div>
				)}
				{getPortalSetting('IsUnsplashEnabled') && (
					<div className='bizmediabank-panel margin-top'>
						<button
							type='button'
							className='panel-toggle'
							aria-expanded={expandedPanel === 'UNSPLASH'}
							onClick={() => togglePanel('UNSPLASH')}
						>
							<span>{t('MEDIABANK.BROWSE_UNSPLASH')}</span>
							<i
								aria-hidden='true'
								className={expandedPanel === 'UNSPLASH' ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'}
							/>
						</button>
						{expandedPanel === 'UNSPLASH' && (
							<div className='unsplash-integration'>
								<BizUnsplash
									onClose={onClose}
									orientation={orientation}
									uploadLocation={uploadLocation}
									settings={settings}
								/>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default MediabankDialog;
